import { useEffect, useState } from "react";
import { Checkbox, Modal, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "../../../components/buttons/buttons";
import Notification from "../../../utility/notification";
import HttpStatus from "../../../utility/status.enum";
import { learnFromConversations, bulkAddKnowledgeBase } from "../../../redux/knowledgeBase/actionCreator";

const CONTENT_TYPE_RESOURCES = 1;
const CONTENT_VISIBILITY_ALWAYS = [1];

/**
 * Modal that fetches the last 3 months of PMS conversations, presents AI-extracted
 * Q&A pairs for review, and lets the host select items to add to the Knowledge Base.
 *
 * Props:
 *   visible   {boolean}  — controls modal open/close
 *   onClose   {function} — called when the modal should close
 *   onAdded   {function} — called after items are successfully added (triggers KB refresh)
 */
const LearnFromConversations = ({ visible, onClose, onAdded }) => {
    const dispatch = useDispatch();
    const { learnLoading, learnSuggestions, learnError, bulkAdding } = useSelector(
        (state) => state.KnowledgeBase
    );

    const [selected, setSelected] = useState(new Set());

    // Fetch suggestions whenever the modal opens
    useEffect(() => {
        if (visible) {
            setSelected(new Set());
            dispatch(learnFromConversations());
        }
    }, [visible]);

    const allSelected =
        learnSuggestions.length > 0 && selected.size === learnSuggestions.length;

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelected(new Set());
        } else {
            setSelected(new Set(learnSuggestions.map((_, i) => i)));
        }
    };

    const toggleItem = (index) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    };

    const handleAddToKB = async () => {
        if (selected.size === 0) return;

        const items = [...selected].map((i) => ({
            title: learnSuggestions[i].question,
            description: learnSuggestions[i].answer,
            content_type: CONTENT_TYPE_RESOURCES,
            content_visibility: CONTENT_VISIBILITY_ALWAYS,
        }));

        const res = await dispatch(bulkAddKnowledgeBase(items));
        if (res?.status === HttpStatus.HTTP_OK) {
            const saved = res?.result?.saved ?? 0;
            Notification("success", `${saved} item${saved !== 1 ? "s" : ""} added to Knowledge Base.`);
            onAdded?.();
            onClose?.();
        } else {
            Notification("error", res?.message || "Failed to add items. Please try again.");
        }
    };

    const renderContent = () => {
        if (learnLoading) {
            return (
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 220, gap: 16 }}>
                    <Spin size="large" />
                    <p className="text-center" style={{ color: "#555", marginTop: 12 }}>
                        Retrieving and analysing your last 3 months of conversations&hellip;
                        <br />
                        <small>This may take a moment.</small>
                    </p>
                </div>
            );
        }

        if (learnError) {
            return (
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 140 }}>
                    <p style={{ color: "#ff4d4f" }}>
                        Something went wrong while fetching conversations. Please try again.
                    </p>
                </div>
            );
        }

        if (!learnSuggestions || learnSuggestions.length === 0) {
            return (
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 140 }}>
                    <p style={{ color: "#888", textAlign: "center" }}>
                        No Q&amp;A suggestions were found in your past conversations.
                        <br />
                        Make sure your PMS is connected and has conversation history.
                    </p>
                </div>
            );
        }

        return (
            <>
                <div className="d-flex align-items-center justify-content-between" style={{ marginBottom: 16 }}>
                    <Checkbox
                        checked={allSelected}
                        indeterminate={selected.size > 0 && !allSelected}
                        onChange={toggleSelectAll}
                    >
                        <strong>Select All ({learnSuggestions.length})</strong>
                    </Checkbox>
                    <span style={{ fontSize: 13, color: "#888" }}>
                        {selected.size} selected
                    </span>
                </div>

                <div style={{ maxHeight: 420, overflowY: "auto" }}>
                    {learnSuggestions.map((pair, i) => (
                        <div
                            key={i}
                            className="d-flex"
                            style={{
                                border: "1px solid #e8e8e8",
                                borderRadius: 8,
                                padding: "12px 16px",
                                marginBottom: 10,
                                background: selected.has(i) ? "#f0f7ff" : "#fff",
                                cursor: "pointer",
                                gap: 12,
                            }}
                            onClick={() => toggleItem(i)}
                        >
                            <Checkbox
                                checked={selected.has(i)}
                                onChange={() => toggleItem(i)}
                                onClick={(e) => e.stopPropagation()}
                                style={{ marginTop: 2 }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ fontWeight: 600, marginBottom: 4, color: "#222", wordBreak: "break-word" }}>
                                    {pair.question}
                                </p>
                                <p style={{ color: "#555", margin: 0, fontSize: 13, wordBreak: "break-word" }}>
                                    {pair.answer}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </>
        );
    };

    return (
        <Modal
            title="Learn from Past Conversations (Last 3 Months)"
            open={visible}
            onCancel={onClose}
            width={680}
            destroyOnClose
            footer={
                learnLoading || learnSuggestions?.length === 0
                    ? null
                    : [
                        <Button
                            key="cancel"
                            htmlType="button"
                            outlined
                            shape="circle"
                            type="primary"
                            className="form-cancel-btn"
                            onClick={onClose}
                            disabled={bulkAdding}
                        >
                            Cancel
                        </Button>,
                        <Button
                            key="add"
                            htmlType="button"
                            shape="circle"
                            type="primary"
                            className="update-btn"
                            disabled={selected.size === 0 || bulkAdding}
                            onClick={handleAddToKB}
                        >
                            {bulkAdding
                                ? "Adding..."
                                : `Add to Knowledge Base${selected.size > 0 ? ` (${selected.size})` : ""}`}
                        </Button>,
                    ]
            }
        >
            {renderContent()}
        </Modal>
    );
};

export default LearnFromConversations;
